import React, { useState, useEffect } from 'react'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import TicketDetailsDialog from './TicketsDetailsDialog'
import { 
    KeyboardArrowLeft, 
    KeyboardArrowRight, 
    KeyboardDoubleArrowLeft, 
    KeyboardDoubleArrowRight 
} from '@mui/icons-material'

// Компонент DatePicker с кастомным календарем
const DatePicker = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [tempDate, setTempDate] = useState(value)

    // Закрытие календаря при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.date-picker-container')) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const formatDisplayDate = (date) => {
        return date.format('MM/DD/YYYY')
    }

    const handleDateSelect = (selectedDate) => {
        setTempDate(selectedDate)
        onChange(selectedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        const clearedDate = dayjs()
        setTempDate(clearedDate)
        onChange(clearedDate)
        setIsOpen(false)
    }

    const handleToday = () => {
        const today = dayjs()
        setTempDate(today)
        onChange(today)
        setIsOpen(false)
    }

    const getCalendarDays = () => {
        const startOfMonth = tempDate.startOf('month')
        const endOfMonth = tempDate.endOf('month')
        const startOfCalendar = startOfMonth.startOf('week')
        const endOfCalendar = endOfMonth.endOf('week')

        const days = []
        let current = startOfCalendar

        while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: current.isSame(tempDate, 'month'),
                isSelected: current.isSame(value, 'day'),
                isToday: current.isSame(dayjs(), 'day')
            })
            current = current.add(1, 'day')
        }

        return days
    }

    const navigateMonth = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'month')
            : tempDate.add(1, 'month')
        setTempDate(newDate)
    }

    const navigateYear = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'year')
            : tempDate.add(1, 'year')
        setTempDate(newDate)
    }
  
    return (
        <div className="form-group date-picker-container" style={{ width: '200px', position: 'relative' }}>
            <label className="form-label">{label}</label>
            <div 
                className="form-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{formatDisplayDate(value)}</span>
                <span style={{ color: 'var(--primary-orange)' }}>📅</span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-md)',
                    boxShadow: 'var(--shadow-card)',
                    marginTop: '4px',
                    width: '280px',
                    minWidth: '280px'
                }}>
                    {/* Заголовок календаря */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--spacing-sm)',
                        gap: '4px'
                    }}>
                        <button
                            onClick={() => navigateYear('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟪
                        </button>
                        <button
                            onClick={() => navigateMonth('prev')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ‹
                        </button>
                        <span style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: '600',
                            color: 'var(--text-light)',
                            flex: 1,
                            textAlign: 'center'
                        }}>
                            {tempDate.format('MMMM YYYY')}
                        </span>
                        <button
                            onClick={() => navigateMonth('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => navigateYear('next')}
                            style={{ 
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                minWidth: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                            onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                        >
                            ⟫
                        </button>
                    </div>

                    {/* Дни недели */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                            <div key={day} style={{
                                padding: '8px 4px',
                                textAlign: 'center',
                                fontSize: 'var(--text-xs)',
                                fontWeight: '600',
                                color: 'var(--text-muted)'
                            }}>
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Календарная сетка */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px'
                    }}>
                        {getCalendarDays().map((day, index) => (
                            <button
                                key={index}
                                onClick={() => handleDateSelect(day.date)}
                                style={{
                                    padding: '8px 4px',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-sm)',
                                    cursor: 'pointer',
                                    background: day.isSelected 
                                        ? 'var(--primary-orange)' 
                                        : day.isToday 
                                            ? 'var(--dark-gray)' 
                                            : 'transparent',
                                    color: day.isSelected 
                                        ? 'var(--white)' 
                                        : !day.isCurrentMonth 
                                            ? 'var(--text-muted)' 
                                            : day.isToday 
                                                ? 'var(--primary-orange)' 
                                                : 'var(--text-light)',
                                    fontWeight: day.isSelected || day.isToday ? '600' : '400',
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = 'var(--dark-gray)'
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!day.isSelected) {
                                        e.target.style.backgroundColor = day.isToday ? 'var(--dark-gray)' : 'transparent'
                                    }
                                }}
                            >
                                {day.date.format('D')}
                            </button>
                        ))}
                    </div>

                    {/* Кнопки действий */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 'var(--spacing-sm)',
                        paddingTop: 'var(--spacing-sm)',
                        borderTop: '1px solid var(--light-gray)',
                        gap: 'var(--spacing-sm)'
                    }}>
                        <button
                            onClick={handleClear}
                            style={{
                                background: 'none',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.borderColor = 'var(--primary-orange)'
                                e.target.style.color = 'var(--primary-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.borderColor = 'var(--light-gray)'
                                e.target.style.color = 'var(--text-light)'
                            }}
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleToday}
                            style={{
                                background: 'var(--primary-orange)',
                                border: '1px solid var(--primary-orange)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--white)',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: 'var(--text-xs)',
                                flex: 1,
                                transition: 'var(--transition-normal)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'var(--dark-orange)'
                                e.target.style.borderColor = 'var(--dark-orange)'
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'var(--primary-orange)'
                                e.target.style.borderColor = 'var(--primary-orange)'
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// Компонент для статуса тикета
const StatusChip = ({ status }) => {
    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending':
                return {
                    background: 'var(--primary-orange)',
                    color: 'var(--white)',
                    border: '1px solid var(--primary-orange)'
                }
            case 'reject':
                return {
                    background: '#dc3545',
                    color: 'var(--white)',
                    border: '1px solid #dc3545'
                }
            case 'refund':
                return {
                    background: '#28a745',
                    color: 'var(--white)',
                    border: '1px solid #28a745'
                }
            default:
                return {
                    background: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    border: '1px solid var(--light-gray)'
                }
        }
    }

    const style = getStatusStyle(status)

    return (
        <span style={{
            ...style,
            padding: '4px 12px',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        }}>
            {status}
        </span>
    )
}

// Компонент пагинации
const PaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 20 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                className="btn btn-outline btn-small"
                onClick={handleFirstPage}
                disabled={safePage === 0}
                style={{ minWidth: '32px', padding: '4px' }}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '16px' }} />
            </button>
            <button 
                className="btn btn-outline btn-small"
                onClick={handlePrevPage}
                disabled={safePage === 0}
                style={{ minWidth: '32px', padding: '4px' }}
            >
                <KeyboardArrowLeft style={{ fontSize: '16px' }} />
            </button>
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {safePage + 1} of {totalPages}
            </span>
            <button 
                className="btn btn-outline btn-small"
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
                style={{ minWidth: '32px', padding: '4px' }}
            >
                <KeyboardArrowRight style={{ fontSize: '16px' }} />
            </button>
            <button 
                className="btn btn-outline btn-small"
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
                style={{ minWidth: '32px', padding: '4px' }}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '16px' }} />
            </button>
        </div>
    )
}

const TicketsAdmin = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(20)
    const [ticketsList, setTicketsList] = React.useState([])
    const [selectedTicket, setSelectedTicket] = React.useState(0)
    const [ticketStatus, setStatus] = React.useState('pending')
    const [startDate, setStartDate] = useState(dayjs())
    const [endDate, setEndDate] = useState(dayjs())
    const [open, setOpen] = React.useState(false)

    const handleProxyIdClick = (proxyId, e) => {
        e.stopPropagation() // Предотвращаем открытие диалога тикета
        if (props.onProxyIdSelect) {
            props.onProxyIdSelect(proxyId)
        }
    }

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ticketsList?.length) : 0

    const handleChangePage = (event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(()=>{
        const callAsync = async() => {
            const ticketsPesponse = await props.apiClient.setToken(getAccessTokenSilently()).geticketsAdmin(page, rowsPerPage, ticketStatus)
            console.log(ticketsPesponse)
            setTicketsList(ticketsPesponse)
        }
        callAsync()   
      },[page, rowsPerPage, ticketStatus, startDate, endDate])
  
    const rowClick = (row) => {
        // console.log(row.id)
        setSelectedTicket(row)
        setOpen(true)
        // Также передаем ID прокси для загрузки информации
        if (props.onProxyIdSelect && row?.lease?.id) {
            props.onProxyIdSelect(row.lease.id)
        }
    }

    const handleStatusChange = (status) => {
        setStatus(status)
    }

    const handleCloseTicket = (status) => {
        setStatus(status)
    }

    return (
        <div style={{
            width: 'calc(100% - 280px)',
            height: '100vh',
            padding: '8px',
            background: 'var(--black)',
            marginLeft: '280px'
        }}>
            {/* Заголовок */}
            <h1 style={{
                background: 'var(--gradient-orange)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginTop: '8px',
                marginBottom: '8px',
                fontSize: 'var(--text-3xl)',
                fontWeight: '700'
            }}>
                Admin Tickets
            </h1>

            {/* Фильтры */}
            <div style={{
                background: 'var(--medium-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-sm)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-lg)',
                flexWrap: 'wrap'
            }}>
                {/* Фильтры по статусу */}
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                    <button 
                        className={`btn ${ticketStatus === 'pending' ? 'btn-primary' : 'btn-outline'} btn-small`}
                        onClick={() => handleStatusChange('pending')}
                    >
                        Pending
                    </button>
                    <button 
                        className={`btn ${ticketStatus === 'refund' ? 'btn-primary' : 'btn-outline'} btn-small`}
                        onClick={() => handleStatusChange('refund')}
                    >
                        Refunded
                    </button>
                    <button 
                        className={`btn ${ticketStatus === 'reject' ? 'btn-primary' : 'btn-outline'} btn-small`}
                        onClick={() => handleStatusChange('reject')}
                    >
                        Rejected
                    </button>
                </div>

                {/* Фильтры по датам */}
                <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                    <DatePicker 
                        label="Start Date"
                        value={startDate}
                        onChange={setStartDate}
                    />
                    <DatePicker 
                        label="End Date"
                        value={endDate}
                        onChange={setEndDate}
                    />
                </div>
            </div>

            {/* Таблица */}
            <div className="table-container">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Creation Date</th>
                            <th>Status</th>
                            <th>Proxy ID</th>
                            <th>Refund Reason</th>
                            <th>Description</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ticketsList?.map((ticket) => (
                            <tr key={ticket.id} onClick={() => rowClick(ticket)} style={{ cursor: 'pointer' }}>
                                <td>{moment(ticket?.created).format('MMM-D H:m, YYYY')}</td>
                                <td>
                                    <StatusChip status={ticket?.status} />
                                </td>
                                <td>
                                    <span 
                                        onClick={(e) => handleProxyIdClick(ticket?.lease?.id, e)}
                                        style={{
                                            color: 'var(--primary-orange)',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            fontWeight: '500'
                                        }}
                                        onMouseEnter={(e) => e.target.style.color = 'var(--dark-orange)'}
                                        onMouseLeave={(e) => e.target.style.color = 'var(--primary-orange)'}
                                    >
                                        {ticket?.lease?.id}
                                    </span>
                                </td>
                                <td>{ticket?.reason}</td>
                                <td>{ticket.message}</td>
                                <td>
                                    <button 
                                        className="btn btn-outline btn-small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            rowClick(ticket, e)
                                        }}
                                        style={{ minWidth: '32px', padding: '4px' }}
                                    >
                                        ℹ️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Пагинация */}
            {ticketsList && ticketsList.length > 0 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'var(--spacing-md)',
                    padding: 'var(--spacing-md)',
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                            Rows per page:
                        </span>
                        <select 
                            value={rowsPerPage}
                            onChange={(e) => handleChangeRowsPerPage(e)}
                            style={{
                                background: 'var(--medium-gray)',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-sm)',
                                color: 'var(--text-light)',
                                padding: '4px 8px',
                                fontSize: 'var(--text-sm)'
                            }}
                        >
                            {[10, 20, 30, 40, 50].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                            {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, ticketsList?.length || 0)} of {ticketsList?.length || 0}
                        </span>
                        <PaginationActions 
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={ticketsList?.length || 0}
                            onPageChange={handleChangePage}
                        />
                    </div>
                </div>
            )}

            <TicketDetailsDialog apiClient={props.apiClient} ticket={selectedTicket} open={open} setOpen={setOpen}/>
        </div>
    )
}

export default TicketsAdmin